import React, { useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';
import { store } from '../../../redux/store';
import { http_get } from '../utils/api_client';
import { API_ENDPOINTS } from '../utils/endpoints';
import BlockRenderer from './blocks/BlockRenderer';
import { brandingStyle, loadGoogleFont } from './branding';
import './DocReader.scss';

// BL-118/119 — published Doc reader. Documentation layout modelled on the
// published-doc mockup: a full-height sidebar with the brand logo at its top and
// the section nav below; a header aligned above the page title holding search +
// auth; then the content column and an "on this page" rail. Text blocks keep
// their Word-doc styling; rich elements reuse the real Path renderers, so a
// walkthrough runs inline. Appearance is driven by `doc.branding` (logo, accent,
// brand name, and search/auth toggles) with safe defaults, so the UI
// customization settings simply populate that object.
const HEADING_TYPES = ['h1', 'h2', 'h3'];

const flattenNav = (sections) => {
  const out = [];
  (sections || []).forEach((s) => {
    out.push({ id: s.id, title: s.title, is_group: s.is_group, depth: 0, blocks: s.blocks || [] });
    (s.children || []).forEach((c) => out.push({ id: c.id, title: c.title, is_group: c.is_group, depth: 1, blocks: c.blocks || [] }));
  });
  return out;
};

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const BookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const DocPreview = ({ id }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState('');
  const [helpful, setHelpful] = useState(null);
  const companySlug = window.location.pathname.split('/')[1];

  useEffect(() => {
    let cancelled = false;
    http_get(API_ENDPOINTS.GET_DOC(id))
      .then(({ data }) => {
        if (cancelled) return;
        setDoc(data);
        const nav = flattenNav(data.content?.sections);
        const first = nav.find((n) => !n.is_group);
        setActiveId(first ? first.id : null);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const nav = useMemo(() => flattenNav(doc?.content?.sections), [doc]);
  const active = nav.find((n) => n.id === activeId);

  // reset the helpful control when the reader moves to a different section
  useEffect(() => { setHelpful(null); }, [activeId]);

  // pull in the chosen web font (no-op if none / already loaded)
  useEffect(() => { loadGoogleFont(doc?.branding?.font); }, [doc]);

  const visibleNav = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return nav;
    return nav.filter((n) => (n.title || '').toLowerCase().includes(q));
  }, [nav, query]);

  const headings = (active?.blocks || []).filter((b) => HEADING_TYPES.includes(b.type) && (b.text || '').trim());

  const scrollToBlock = (blockId) => {
    const el = document.getElementById(`blk-${blockId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) return <div className="doc-r-load">Loading…</div>;
  if (error || !doc) return <div className="doc-r-load">This doc isn’t available.</div>;

  const brand = doc.branding || {};
  const showSearch = brand.show_search !== false;
  const brandName = brand.brand_name || doc.name || 'Docs';
  const shellStyle = brandingStyle(brand);

  return (
    <Provider store={store}>
      <div className="doc-r-shell" style={shellStyle}>
        <div className="doc-r">
          <aside className="doc-r-side">
            <div className={`doc-r-logo ${brand.logo_url ? 'doc-r-logo--img' : ''}`}>
              {brand.logo_url ? (
                <img className="doc-r-logo__img" src={brand.logo_url} alt={brandName} />
              ) : (
                <>
                  <span className="doc-r-logo__ic"><BookIcon /></span>
                  <span className="doc-r-logo__t">{brandName}</span>
                </>
              )}
            </div>
            <nav className="doc-r-nav">
              {visibleNav.map((n) => (n.is_group ? (
                <div key={n.id} className={`doc-r-group depth-${n.depth}`}>{n.title}</div>
              ) : (
                <div key={n.id} className={`doc-r-link depth-${n.depth} ${activeId === n.id ? 'on' : ''}`} onClick={() => setActiveId(n.id)}>{n.title}</div>
              )))}
              {visibleNav.length === 0 && <div className="doc-r-navempty">{query.trim() ? 'No matches.' : 'No sections yet.'}</div>}
            </nav>
          </aside>

          <div className="doc-r-body">
            {showSearch && (
              <header className="doc-r-top">
                <div className="doc-r-search">
                  <span className="doc-r-search__ic"><SearchIcon /></span>
                  <input value={query} placeholder="Search documentation…" onChange={(e) => setQuery(e.target.value)} />
                </div>
              </header>
            )}

            <div className="doc-r-cols">
              <main className="doc-r-main">
                {!active ? (
                  <div className="doc-r-empty">This doc has no content yet.</div>
                ) : (
                  <div className="doc-r-content">
                    <h1 className="doc-r-page-title">{active.title}</h1>
                    {(active.blocks || []).map((b) => <BlockRenderer key={b.id} block={b} companySlug={companySlug} />)}
                    {(active.blocks || []).length === 0 && <div className="doc-r-empty">Nothing on this page yet.</div>}
                  </div>
                )}
              </main>

              <aside className="doc-r-toc">
                {headings.length > 0 && (
                  <div className="doc-r-toc__sec">
                    <div className="doc-r-toc__h">On this page</div>
                    {headings.map((h) => (
                      <div key={h.id} className={`doc-r-toc__link is-${h.type}`} onClick={() => scrollToBlock(h.id)}>{h.text}</div>
                    ))}
                  </div>
                )}
                <div className="doc-r-toc__sec">
                  <div className="doc-r-toc__h">Was this helpful?</div>
                  {helpful ? (
                    <div className="doc-r-help__thanks">Thanks for the feedback.</div>
                  ) : (
                    <div className="doc-r-help__row">
                      <button type="button" className="doc-r-help__b" onClick={() => setHelpful('yes')}>Yes</button>
                      <button type="button" className="doc-r-help__b" onClick={() => setHelpful('no')}>No</button>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </Provider>
  );
};

export default DocPreview;
